import React, { useCallback, useEffect } from "react";
import {
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSearchParams, Navigate } from "react-router-dom";
import SearchBar from "../../common/SearchBar/SearchBar";
import { useMembership } from "../../../contexts/MembershipContext";
import { SearchResult } from "../TopBar/useGlobalSearch";
import Logo from "../../shared/Logo";
import ProjectionsLoader from "./ProjectionsLoader";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { MOBILE_TOPBAR_HEIGHT } from "../../../contexts/SidebarContext";
import { isNative } from "../../../lib/platform";
import { getSymbolImageUrl } from "../../../lib/symbolImage";
import { SERIF_DISPLAY } from "../../Chat/ChatHero";
import { heroRise } from "../../Chat/chatMotion";
import {
  NativeLandingContainer,
  NativeLandingHeader,
  NativeListRow,
  NativeSectionLabel,
} from "../../Native/NativeLanding";
import { Stack } from "@mui/material";

const ProjectionsPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMembershipActive } = useMembership();

  // Get ticker from URL params
  const ticker = searchParams.get("ticker");

  // Define all hooks before any conditional returns
  const handleSelectCompany = useCallback((item: SearchResult) => {
    setSearchParams({ ticker: item.symbol });
  }, [setSearchParams]);

  const handleStockClick = useCallback((symbol: string) => {
    setSearchParams({ ticker: symbol });
  }, [setSearchParams]);

  // Feature usage: a DCF projection was opened for a ticker (via search, a popular
  // pick, or a direct link). Keyed on ticker so it fires once per projection loaded.
  const { trackFeature } = useAnalytics();
  useEffect(() => {
    // Projections are member-only; the page redirects non-members, so without this
    // guard a deep-linked non-member would fire the event before being bounced.
    if (ticker && isMembershipActive) {
      trackFeature("dcf_projection", { ticker });
    }
  }, [ticker, isMembershipActive, trackFeature]);

  // Guard: redirect non-members away from projections
  if (!isMembershipActive) {
    return <Navigate to="/dashboard" replace />;
  }

  // If ticker exists, show the projections loader (handles data fetching and currency context)
  if (ticker) {
    return <ProjectionsLoader ticker={ticker} />;
  }

  // Otherwise show the landing/home page

  // Popular stocks for DCF analysis
  const popularStocks = [
    {
      symbol: "AAPL",
      name: "Apple",
      logo: getSymbolImageUrl("AAPL"),
    },
    {
      symbol: "NVDA",
      name: "Nvidia",
      logo: getSymbolImageUrl("NVDA"),
    },
    {
      symbol: "MSFT",
      name: "Microsoft",
      logo: getSymbolImageUrl("MSFT"),
    },
    {
      symbol: "GOOGL",
      name: "Alphabet",
      logo: getSymbolImageUrl("GOOGL"),
    },
    {
      symbol: "TSLA",
      name: "Tesla",
      logo: getSymbolImageUrl("TSLA"),
    },
  ];

  // Native app: compact left-aligned header (no logo), search, and dense
  // company rows — vertically centred between the app bars.
  if (isNative()) {
    return (
      <NativeLandingContainer>
        <NativeLandingHeader
          title="Projections"
          subtitle="Calculate any company's intrinsic value with DCF"
        />

        <Box data-tour="projections-search">
          <SearchBar
            placeholder="Search any stock ticker or company..."
            width="100%"
            variant="chatgpt"
            onSelectItem={handleSelectCompany}
          />
        </Box>

        <NativeSectionLabel>Popular stocks</NativeSectionLabel>

        <Stack spacing={1.25}>
          {popularStocks.map((stock) => (
            <NativeListRow
              key={stock.symbol}
              leading={
                <Logo src={stock.logo} alt={`${stock.name} logo`} ticker={stock.symbol} size={28} />
              }
              title={stock.name}
              subtitle={stock.symbol}
              onClick={() => handleStockClick(stock.symbol)}
            />
          ))}
        </Stack>
      </NativeLandingContainer>
    );
  }

  // Web + mobile web: mirrors the chat/research/compare heroes — same serif
  // heading, viewport math, entrance stagger, and logo-tile row — with the
  // projections disclaimer kept pinned at the foot of the viewport.
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        height: { xs: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px)`, lg: "100vh" },
        overflowY: "auto",
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          width: "100%",
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        {/* my: "auto" centres the hero in the leftover space so the pinned
            disclaimer below barely shifts it off the other tabs' centre. */}
        <Box
          sx={{
            my: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            py: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ mb: 3, ...heroRise(0) }}>
            <img
              src={isDark ? "/insightLongLogoPrimaryWhiteText.svg" : "/insightLongLogoPrimary.svg"}
              alt="Insight Analytics"
              style={{ height: 48, width: "auto", maxWidth: "100%" }}
            />
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF_DISPLAY,
              fontWeight: 500,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
              color: isDark ? "#ffffff" : "#1d1d1f",
              mb: { xs: 3.5, md: 4.5 },
              ...heroRise(0.05),
            }}
          >
            What would you like to value?
          </Typography>

          <Box data-tour="projections-search" sx={{ width: "100%", ...heroRise(0.14) }}>
            <SearchBar
              placeholder="Search any stock to project its intrinsic value..."
              width="100%"
              variant="chatgpt"
              onSelectItem={handleSelectCompany}
            />
          </Box>

          {/* Compact company tiles, identical to the other hero rows: logo
              only, names in tooltips, one uniform row under the search. */}
          <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {popularStocks.map((stock, index) => (
              <Tooltip key={stock.symbol} title={`Value ${stock.name}`} arrow>
                <Box
                  component="button"
                  aria-label={`Value ${stock.name}`}
                  onClick={() => handleStockClick(stock.symbol)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    p: 0,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: isDark
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
                    transition: "border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
                      borderColor: isDark ? "rgba(34, 197, 94, 0.45)" : "rgba(34, 197, 94, 0.4)",
                      transform: "scale(1.05)",
                    },
                    "&:active": { transform: "scale(0.97)" },
                    "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
                    ...heroRise(0.2 + index * 0.045),
                  }}
                >
                  <Logo src={stock.logo} alt={stock.name} ticker={stock.symbol} size={22} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: isDark ? "#52525b" : "#a1a1aa",
            fontSize: { xs: "0.65rem", md: "0.7rem" },
            lineHeight: 1.6,
            ...heroRise(0.3),
          }}
        >
          Projections are hypothetical scenarios for informational purposes only and do not constitute financial advice.
          <br />
          Consult a qualified advisor before making investment decisions.
        </Typography>
      </Box>
    </Box>
  );
};

export default ProjectionsPage;
